import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleUnit } from '../../types/schema';
import { useCostTracking, CostTrackingProvider } from '../../contexts/CostTrackingContext';
import type { ExpenseCategoryEntry, ExpenseRecord } from '../../types/costTrackingTypes';
import { umSinceBeginning } from '../pages-equipment/equipmentFieldUtils';
import { UnitUsagePanel } from '../pages-equipment/EquipmentUsageTab';
import { calculateITDCostPerUM, expenseWorkDescriptionLabel, formatExpenseDate } from '../../utils/costTrackingUtils';
import { PM_INTERNAL_SERVICE_CATEGORY } from '../../utils/pmCostTracking';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { Btn, Card, DataTable, type DataTableColumn } from '../ui';
import '../../css/CostTrackingDashboard.css';

interface SingleUnitCostTabProps {
    unitId: string;
    unitName: string;
    currentUMCounterValue: number;
    unitOfMeasure: string;
    onViewExpense?: (expenseId: string) => void;
    showModuleLink?: boolean;
}

const formatCurrency = (value: number) =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function expenseCategoryNames(record: ExpenseRecord): string {
    const names: string[] = [];
    if (record.expenseCategories) {
        try {
            const cats: ExpenseCategoryEntry[] = JSON.parse(record.expenseCategories);
            for (const cat of cats) {
                if (cat.categoryName && !names.includes(cat.categoryName)) {
                    names.push(cat.categoryName);
                }
            }
        } catch {
            // Leave the names already collected.
        }
    }
    if (
        (record.pmServiceLevelName || (record.pmExpense ?? 0) > 0)
        && !names.includes(PM_INTERNAL_SERVICE_CATEGORY)
    ) {
        names.unshift('PM Service');
    }
    return names.join(', ') || '—';
}

/**
 * Inner component that consumes CostTrackingContext.
 * Displays cost summary card with total cost, ITD $/UM, and category breakdown.
 *
 * Validates: Requirements 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */
export const UnitCostPanel = ({
    unitId,
    unitName,
    currentUMCounterValue,
    unitOfMeasure,
    onViewExpense,
    showModuleLink = true,
}: SingleUnitCostTabProps) => {
    const navigate = useNavigate();
    const { getTotalCostForUnit, getCostBreakdownForUnit, getExpensesForUnit, isLoading } = useCostTracking();

    const totalCost = getTotalCostForUnit(unitId);
    const itdCostPerUM = calculateITDCostPerUM(totalCost, currentUMCounterValue);
    const breakdown = getCostBreakdownForUnit(unitId);
    const expenses = useMemo(
        () =>
            [...getExpensesForUnit(unitId)].sort((left, right) =>
                (right.dateOfService || '').localeCompare(left.dateOfService || ''),
            ),
        [getExpensesForUnit, unitId],
    );

    const umLabel = displayUnitOfMeasure(unitOfMeasure) || 'UM';

    const columns = useMemo<DataTableColumn<ExpenseRecord>[]>(
        () => [
            {
                id: 'date',
                header: 'Date',
                renderCell: (record) => formatExpenseDate(record.dateOfService),
            },
            {
                id: 'description',
                header: 'Description',
                mobileCardPrimary: true,
                renderCell: (record) => expenseWorkDescriptionLabel(record.workDescription) || '—',
            },
            {
                id: 'category',
                header: 'Category',
                renderCell: (record) => expenseCategoryNames(record),
            },
            {
                id: 'total',
                header: 'Total',
                align: 'right',
                renderCell: (record) => formatCurrency(record.totalExpense),
            },
            {
                id: 'actions',
                header: 'Actions',
                headerLabel: 'Actions',
                align: 'center',
                mobileCardAction: true,
                renderCell: (record) => (
                    <Btn
                        variant="link"
                        buttonSize="sm"
                        buttonText="View"
                        ariaLabel={`View cost recorded ${formatExpenseDate(record.dateOfService)}`}
                        buttonOnClick={() => {
                            if (onViewExpense) {
                                onViewExpense(record.id);
                                return;
                            }
                            navigate(`/cost-tracking?expense=${record.id}`);
                        }}
                    />
                ),
            },
        ],
        [navigate, onViewExpense],
    );

    return (
        <div className="ct-unit-tab">
            <UnitUsagePanel
                unitId={unitId}
                unitName={unitName}
                defaultPreset="ytd"
                showCostModuleLink={false}
                wrapInTab={false}
            />

            {isLoading ? (
                <div className="ct-unit-tab-loading" role="status" aria-live="polite">
                    <p>Loading cost data...</p>
                </div>
            ) : (
                <>
                    <Card variant="card-sm" className="ct-unit-summary-card">
                        <h2>Cost Summary</h2>

                        <div className="ct-unit-summary-metrics">
                            <div className="ct-unit-metric">
                                <span className="ct-unit-metric-label">Total Recorded Cost</span>
                                <span className="ct-unit-metric-value">{formatCurrency(totalCost)}</span>
                            </div>
                            <div className="ct-unit-metric">
                                <span className="ct-unit-metric-label">ITD $ / {umLabel}</span>
                                <span className="ct-unit-metric-value">
                                    {formatCurrency(itdCostPerUM)} / {umLabel}
                                </span>
                            </div>
                        </div>

                        {/* Category Breakdown */}
                        {breakdown.length > 0 && (
                            <div className="ct-unit-breakdown">
                                <h3>Cost Breakdown by Category</h3>
                                <div className="ct-unit-breakdown-list">
                                    {breakdown.map((cat) => (
                                        <div key={cat.categoryName} className="ct-unit-breakdown-row">
                                            <span className="ct-unit-breakdown-name">{cat.categoryName}</span>
                                            <span className="ct-unit-breakdown-cost">
                                                {formatCurrency(cat.totalCost)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {breakdown.length === 0 && (
                            <p className="ct-unit-empty-msg">No costs have been recorded for this unit.</p>
                        )}
                    </Card>

                    <Card variant="card-sm" className="ct-unit-records">
                        <h2>Recorded costs</h2>
                        <p className="ct-unit-records-note">
                            Daily Service, Preventive Maintenance, and other costs recorded for this unit.
                        </p>
                        <DataTable
                            ariaLabel="Recorded costs for this unit"
                            columns={columns}
                            rows={expenses}
                            getRowKey={(record) => record.id}
                            mobileCardLayout
                            emptyState={
                                <p className="ct-unit-empty-msg">No costs have been recorded for this unit.</p>
                            }
                        />
                    </Card>

                    {showModuleLink && (
                        <div className="ct-unit-tab-actions">
                            <Btn
                                variant="primary"
                                buttonShape="pill"
                                buttonText="Open Expense / Cost Tracking"
                                buttonOnClick={() => navigate('/cost-tracking')}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * SingleUnitCostTab wraps the inner component with CostTrackingProvider
 * so the context is available when rendered from SingleUnitContent.
 *
 * Validates: Requirements 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */
export const SingleUnitCostTab = ({ unit }: { unit: SingleUnit }) => {
    return (
        <CostTrackingProvider>
            <UnitCostPanel
                unitId={unit.id}
                unitName={unit.name}
                currentUMCounterValue={umSinceBeginning(unit)}
                unitOfMeasure={unit.unitOfMeasure ?? 'UM'}
            />
        </CostTrackingProvider>
    );
};
